/**
 * Firestore Auth Guard Operators
 * Prevents permission-denied errors by ensuring auth readiness before Firestore operations
 */

import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  switchMap,
  retryWhen,
  take,
  tap,
  catchError,
  timeout,
  scan,
  delayWhen,
  concatMap,
  first,
  filter,
} from 'rxjs/operators';
import { FirebaseError } from 'firebase/app';

/**
 * RxJS operator that waits for auth to be ready before emitting.
 * Prevents race condition where Firestore operations start before token is valid.
 *
 * Polls `authReadyCheck` every 500 ms until it returns `true`, then subscribes
 * to the source observable.  Falls back to proceeding after `maxWaitMs` so the
 * UI is never permanently blocked (e.g. if the CF that fetches the custom token
 * fails on a slow iOS/Safari connection).
 *
 * NOTE: This operator should be applied BEFORE the Firestore source observable
 * is created (i.e. use `switchMap(() => firestoreObs)` pattern), not piped
 * onto an already-started stream.  For new code, prefer gating directly on
 * `authStateService.customTokenReady$` which is the definitive "token stored"
 * signal.
 */
export function waitForAuthReady(
  authReadyCheck: () => Observable<boolean>,
  maxWaitMs: number = 5000
) {
  return <T>(source: Observable<T>): Observable<T> => {
    const pollMs = 500;

    // Poll authReadyCheck every pollMs until it emits true, then subscribe source.
    // timer(0, pollMs) emits immediately then on each interval so there is no
    // initial dead-time before the first check.
    const ready$ = timer(0, pollMs).pipe(
      concatMap(() => authReadyCheck()),
      first((isReady) => isReady === true),
      timeout({
        each: maxWaitMs,
        with: () => {
          return of(true);
        },
      }),
      catchError((err) => {
        return of(true);
      })
    );

    return ready$.pipe(switchMap(() => source));
  };
}

/**
 * RxJS operator that handles permission-denied errors with retry logic.
 * Waits for tokenRefreshInProgress$ to be false before retrying, so the
 * retry always happens after the in-flight token refresh has completed.
 */
export function handlePermissionDeniedError(
  tokenRefreshFn?: () => Observable<any>,
  maxRetries: number = 3,
  tokenRefreshInProgress$?: BehaviorSubject<boolean>
) {
  return <T>(source: Observable<T>): Observable<T> => {
    return source.pipe(
      retryWhen((errors) =>
        errors.pipe(
          scan((retryCount, error) => {
            const isPermissionDenied =
              error instanceof FirebaseError && error.code === 'permission-denied';

            if (!isPermissionDenied || retryCount >= maxRetries) {
              console.error(
                `[handlePermissionDeniedError] Max retries (${maxRetries}) reached or non-permission error`,
                error
              );
              throw error;
            }

            console.warn(
              `[handlePermissionDeniedError] Permission denied, retrying (attempt ${retryCount + 1}/${maxRetries})`
            );
            return retryCount + 1;
          }, 0),
          delayWhen((retryCount) => {
            // Wait until token refresh is no longer in progress
            const waitForRefresh$: Observable<any> = tokenRefreshInProgress$
              ? tokenRefreshInProgress$.pipe(
                  filter((inProgress) => !inProgress),
                  first()
                )
              : timer(0);

            // On first retry, also call the token refresh function if provided
            const refresh$ =
              tokenRefreshFn && retryCount === 1
                ? tokenRefreshFn().pipe(catchError(() => of(null)))
                : of(null);

            return waitForRefresh$.pipe(switchMap(() => refresh$));
          })
        )
      ),
      catchError((error) => {
        console.error('[handlePermissionDeniedError] Final error after retries:', error);
        return throwError(() => error);
      })
    );
  };
}

/**
 * Detects permission-denied errors from Firestore operations
 * Logs diagnostic info for debugging
 */
export function detectPermissionDeniedError<T>() {
  return (source: Observable<T>): Observable<T> => {
    return source.pipe(
      catchError((error) => {
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          console.error('[detectPermissionDeniedError] Permission denied error caught:', {
            code: error.code,
            message: error.message,
            timestamp: new Date().toISOString(),
          });
        }
        return throwError(() => error);
      })
    );
  };
}